import {
  type NshiftApiResponse,
  type NshiftCreateSessionRequest,
  type NshiftCreateSessionResponse,
  type NshiftCreateShipmentQueryParams,
  type NshiftCreateShipmentRequest,
  type NshiftCreateShipmentResponse,
  type NshiftDeliveryOptionsRequest,
  type NshiftDeliveryOptionsResponse,
  type NshiftErrorResponse
} from 'ecosystem'
import { createLogger, createUrl, resolveUrlPath } from 'shared-utils'
import { apiCall, type ApiCallResponse } from '../../utils/fetching'
import { NSHIFT_CHECKOUT_API_URL, NSHIFT_CHECKOUT_CONNECTION_ID } from '../constants'
import { createNshiftAccessHeaders } from '../utils'

const headersJson = {
  'Content-Type': 'application/json'
}

const logger = createLogger('nshift', 'checkout')

const nshiftApiCall = async <Data>(
  url: string,
  {
    params,
    ...props
  }: RequestInit & {
    params?: Record<
      string,
      | undefined
      | null
      | string
      | number
      | boolean
      | (string | number | boolean | null | undefined)[]
    >
  }
): Promise<ApiCallResponse<Data>> => {
  if (!NSHIFT_CHECKOUT_API_URL) {
    throw new Error('NSHIFT_CHECKOUT_API_URL is not provided')
  }

  const finalUrl = resolveUrlPath(NSHIFT_CHECKOUT_API_URL, createUrl(url, params))

  const body = (props.body as string) ?? ''
  logger.log('[REQ]', props.method, finalUrl, body ? JSON.parse(body) : '')

  const { error, data, response } = await apiCall<NshiftApiResponse<Data>>(finalUrl, props)

  if (error || !response) {
    logger.log(`[RES] ${response?.status} error`, props.method, finalUrl, error)
    return {
      data: null,
      error,
      response
    }
  }

  if (response.ok) {
    logger.log(`[RES] ${response.status} success`, props.method, finalUrl, data)
    return {
      data: data as Data,
      error: null,
      response
    }
  }

  if (!data) {
    logger.log(`[RES] ${response.status} error`, props.method, finalUrl, 'Unknown error')
    return {
      data: null,
      error: 'Unknown error',
      response
    }
  }

  if ('detail' in (data as NshiftErrorResponse)) {
    const errorData = data as NshiftErrorResponse
    const errorMessage =
      errorData.issues
        ?.map((issue) => issue.issueCode || issue.description)
        ?.filter(Boolean)
        .join(', ') ||
      errorData.detail ||
      errorData.title ||
      String(errorData.status)
    logger.log(`[RES] ${response.status} error with detail`, props.method, finalUrl, errorMessage)
    return {
      data: null,
      error: errorMessage,
      response
    }
  }

  logger.log(`[RES] ${response.status} success`, props.method, finalUrl, data)

  return {
    data: data as Data,
    error: null,
    response
  }
}

/**
 * Create Session
 * @see https://helpcenter.nshift.com/hc/en-us/articles/13495941082396-Getting-started-with-nShift-Checkout-API
 */
const createSession = async (payload: NshiftCreateSessionRequest) => {
  if (!NSHIFT_CHECKOUT_CONNECTION_ID) {
    throw new Error('NSHIFT_CHECKOUT_CONNECTION_ID is not provided')
  }

  const { data: sessionDetails, error: sessionError } =
    await nshiftApiCall<NshiftCreateSessionResponse>(
      `/checkout/options/v1/sessions/${NSHIFT_CHECKOUT_CONNECTION_ID}`,
      {
        method: 'POST',
        headers: {
          ...headersJson,
          ...(await createNshiftAccessHeaders())
        },
        body: JSON.stringify(payload)
      }
    )

  if (!sessionDetails || sessionError) {
    throw new Error(sessionError || 'Unknown error')
  }

  return sessionDetails
}

/**
 * fetch delivery options
 * @see https://helpcenter.nshift.com/hc/en-us/articles/13495941082396-Getting-started-with-nShift-Checkout-API
 */
const getDeliveryOptions = async ({ sessionId, ...payload }: NshiftDeliveryOptionsRequest) =>
  nshiftApiCall<NshiftDeliveryOptionsResponse>(
    `/checkout/options/v1/shipping-options/${sessionId}`,
    {
      method: 'POST',
      headers: {
        ...headersJson,
        ...(await createNshiftAccessHeaders())
      },
      body: JSON.stringify(payload)
    }
  )

/**
 * Create Partial Shipment
 * @see https://helpcenter.nshift.com/hc/en-us/articles/13495941082396-Getting-started-with-nShift-Checkout-API
 */
const createPartialShipment = async (
  payload: NshiftCreateShipmentRequest,
  params?: NshiftCreateShipmentQueryParams
) =>
  nshiftApiCall<NshiftCreateShipmentResponse>('/checkout/shipments/v1/shipments', {
    method: 'POST',
    headers: {
      ...headersJson,
      ...(await createNshiftAccessHeaders())
    },
    body: JSON.stringify(payload),
    params: {
      'extended-result': 'true',
      'send-to-book-and-print': 'true',
      ...params
    }
  })

export const nshiftCheckoutApi = {
  createSession,
  getDeliveryOptions,
  createPartialShipment
}
